import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import PageHeader from '../components/PageHeader';
import DataState from '../components/DataState';
import StatCard from '../components/StatCard';
import { fetchInterestSignups } from '../api/interestSignups';
import { colors } from '../design-system/tokens';
import { downloadCsv } from '../utils/csv';

/**
 * 출시 알림 신청 (master 전용 — launchSignups.read)
 *
 * 홈(mogoo.co.kr) "출시 알림 받기" + 카테고리 "오픈 알림 받기"로 쌓인 신청자 명단.
 * 플랫폼 스코프 데이터라 매장(store) 선택과 무관하다.
 *
 * PII 정책:
 *  - 이메일/전화는 기본 마스킹으로 표시하고, 토글로만 전체보기 (어깨너머 유출 방지)
 *  - CSV는 출시 알림 발송용 원본 값 — master 전용 화면에서만 내려받을 수 있다
 */

// 카테고리 코드 → 한국어 (admin InterestSignupsPage와 동기화 유지)
const CATEGORY_LABELS = {
  carwash: '세차',
  gym: '헬스장',
  academy: '학원',
  laundry: '세탁소',
  cafe: '카페',
  electronics: '전자제품',
  pet: '반려동물',
};

const TYPE_FILTERS = [
  { key: 'all', label: '전체' },
  { key: 'launch', label: '출시 알림' },
  { key: 'category', label: '카테고리 관심' },
];

/** ab***@gmail.com — 로컬 파트 앞 2자만 남기고 마스킹 */
export function maskEmail(email) {
  if (!email) return '-';
  const at = email.indexOf('@');
  if (at <= 0) return '***';
  const local = email.slice(0, at);
  const head = local.slice(0, Math.min(2, local.length));
  return `${head}***${email.slice(at)}`;
}

/** 01012345678 → 010-****-5678 */
export function maskPhone(phone) {
  if (!phone) return '-';
  const digits = String(phone).replace(/[^0-9]/g, '');
  if (digits.length < 7) return '***';
  return `${digits.slice(0, 3)}-****-${digits.slice(-4)}`;
}

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('ko-KR', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const typeChip = (type) =>
  type === 'launch'
    ? { label: '출시 알림', style: colors.status.info }
    : { label: '카테고리', style: colors.status.neutral };

const LaunchSignups = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // MUI 0-base
  const [limit, setLimit] = useState(50);
  const [typeFilter, setTypeFilter] = useState('all');
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, limit };
      if (typeFilter !== 'all') params.type = typeFilter;
      const data = await fetchInterestSignups(params);
      setRows(Array.isArray(data?.items) ? data.items : []);
      setTotal(data?.total ?? 0);
    } catch (e) {
      setError(
        e?.response?.data?.message || '신청자 명단을 불러오지 못했습니다.',
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const launchCount = useMemo(
    () => (typeFilter === 'launch' ? total : null),
    [typeFilter, total],
  );

  const handleCsv = () => {
    // CSV는 발송용 원본 값 — 현재 페이지에 로드된 rows 기준
    const ok = downloadCsv(
      `mogoo-signups-${typeFilter}-${new Date().toISOString().slice(0, 10)}.csv`,
      rows.map((r) => ({
        신청일시: formatDateTime(r.createdAt),
        유형: r.type === 'launch' ? '출시 알림' : '카테고리',
        카테고리: r.category ? CATEGORY_LABELS[r.category] || r.category : '-',
        이메일: r.email || '',
        전화: r.phone || '',
        유입: r.source || '',
      })),
    );
    if (!ok) {
      // rows가 비어 있으면 다운로드가 조용히 무시되므로 사용자에게 알린다
      setError('내보낼 신청 내역이 없습니다.');
    }
  };

  return (
    <Box>
      <PageHeader
        title="출시 알림 신청"
        description="홈페이지·앱에서 출시 알림과 카테고리 오픈 알림을 신청한 명단입니다. 매장과 무관한 플랫폼 데이터예요."
      />

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label={typeFilter === 'all' ? '전체 신청' : TYPE_FILTERS.find((f) => f.key === typeFilter)?.label}
          value={`${total.toLocaleString('ko-KR')}건`}
          caption={launchCount != null ? '앱 출시 시 알림 발송 대상' : undefined}
        />
      </Stack>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}
        >
          {TYPE_FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={f.label}
              clickable
              color={typeFilter === f.key ? 'primary' : 'default'}
              variant={typeFilter === f.key ? 'filled' : 'outlined'}
              onClick={() => {
                setTypeFilter(f.key);
                setPage(0);
              }}
            />
          ))}
          <Box sx={{ flex: 1 }} />
          <Tooltip title={revealed ? '이메일·전화를 다시 마스킹합니다' : '이메일·전화 전체를 표시합니다'}>
            <Button
              size="small"
              startIcon={revealed ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
              onClick={() => setRevealed((v) => !v)}
            >
              {revealed ? '마스킹' : '전체보기'}
            </Button>
          </Tooltip>
          <Button
            size="small"
            startIcon={<DownloadRoundedIcon />}
            onClick={handleCsv}
            disabled={loading || rows.length === 0}
          >
            CSV
          </Button>
          <Button size="small" startIcon={<RefreshRoundedIcon />} onClick={load}>
            새로고침
          </Button>
        </Stack>
      </Paper>

      <DataState
        loading={loading}
        error={error}
        onRetry={load}
        empty={rows.length === 0}
        emptyText="아직 신청이 없습니다."
        skeleton={{ rows: 6, cols: 6 }}
      >
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>신청일시</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>유형</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>카테고리</TableCell>
                  <TableCell>이메일</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>전화</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>유입</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const chip = typeChip(row.type);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.createdAt)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{
                            bgcolor: chip.style.bg,
                            color: chip.style.fg,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.category
                          ? CATEGORY_LABELS[row.category] || row.category
                          : '-'}
                      </TableCell>
                      <TableCell>
                        {revealed ? row.email || '-' : maskEmail(row.email)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {revealed ? row.phone || '-' : maskPhone(row.phone)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {row.source || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="페이지당"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
          />
        </Paper>
      </DataState>

      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: colors.text.muted }}>
        이메일·전화는 알림 발송 목적 외 사용을 금지합니다. CSV 파일은 발송 후 파기해 주세요.
      </Typography>
    </Box>
  );
};

export default LaunchSignups;
